import { useEffect, useState, useMemo } from 'react';
import {
  listarLancamentos,
  rodarContabilizacao, criarLancamento, planoCliente,
  obterLancamento, atualizarLancamento, excluirLancamento,
} from '../api';
import { AvisoForaDaCarteira } from '../components/CompetenciaFinal';
import ImportadorLancamentos from '../components/ImportadorLancamentos';
import { PageHeader, Loading, Empty, Alert } from '../components/common';
import { fmtMoeda, fmtData, mesAtual, anosOpcoes, mesOpcoes } from '../lib/format';

export default function Lancamentos({ clienteId }) {
  const comp = mesAtual();
  const [ano, mes] = comp.split('-').map(Number);
  const [filtroAno, setFiltroAno] = useState(ano);
  const [filtroMes, setFiltroMes] = useState(mes);
  const [editando, setEditando] = useState(null);   // lancamento em edicao
  const [modoClassico, setModoClassico] = useState(() => {
    try { return localStorage.getItem('flow_lctos_classico') === '1'; } catch { return false; }
  });
  // Lancamento se altera enquanto o mes esta aberto. O controle e o
  // fechamento do periodo, nao um fluxo de aprovacao.
  const abrirEdicao = async (l) => {
    try {
      const completo = await obterLancamento(clienteId, l.id);   // traz as partidas
      setEditando(completo);
      setFormAberto(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { alert('Erro ao abrir o lançamento: ' + e.message); }
  };

  const excluirLcto = async (l) => {
    if (!confirm(`Excluir o lançamento "${(l.historico || '').slice(0, 60)}"?`)) return;
    try {
      await excluirLancamento(clienteId, l.id);
      setEditando(null);
      carregar();
    } catch (e) { alert('Erro: ' + e.message); }
  };

  // Livro Diário da competência, pronto para impressão
  const imprimirDiario = () => {
    const linhas = lctos.map(l => {
      const ps = (l.partidas || []).map(pa => `
        <tr class="p">
          <td></td><td></td>
          <td class="cta" style="padding-left:${pa.lado === 'C' ? 34 : 12}px">
            <span class="red">${pa.conta_reduzido ?? ''}</span>
            <span class="cod">${pa.conta_codigo}</span>
            ${pa.lado === 'C' ? '<i>a</i> ' : ''}${pa.conta_nome}
          </td>
          <td class="v">${pa.lado === 'D' ? fmtMoeda(pa.valor) : ''}</td>
          <td class="v">${pa.lado === 'C' ? fmtMoeda(pa.valor) : ''}</td>
        </tr>`).join('');
      return `
        <tr class="h">
          <td class="dt">${fmtData(l.data_lcto)}</td>
          <td class="n">#${l.numero ?? l.id}</td>
          <td class="hist" colspan="3"><b>${l.historico}</b></td>
        </tr>${ps}`;
    }).join('');
    const totD = lctos.reduce((s, l) => s + Number(l.valor_total || 0), 0);
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html lang="pt-BR"><head><meta charset="utf-8">
      <title>Livro Diário ${String(filtroMes).padStart(2, '0')}/${filtroAno}</title><style>
      @page { size: A4; margin: 14mm; }
      body { font-family: -apple-system, 'Segoe UI', sans-serif; font-size: 10pt; color: #111; }
      h1 { font-size: 14pt; margin: 0 0 2px; } .sub { color: #666; font-size: 9pt; margin-bottom: 14px; }
      table { width: 100%; border-collapse: collapse; }
      th { text-align: left; font-size: 8pt; text-transform: uppercase; letter-spacing: .08em;
           color: #666; border-bottom: 1px solid #999; padding: 6px 8px; }
      th.v, td.v { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; padding-right: 10px; }
      tr.h td { border-top: 1px solid #ccc; padding: 8px; }
      tr.p td { padding: 2px 8px; font-size: 9.5pt; }
      td.dt, td.n { white-space: nowrap; color: #444; }
      .red { color: #999; font-size: 8pt; margin-right: 6px; }
      .cod { font-variant-numeric: tabular-nums; color: #666; margin-right: 8px; }
      tfoot td { border-top: 2px solid #111; font-weight: 700; padding: 8px; }
      tr { break-inside: avoid; }
      </style></head><body>
      <h1>Livro Diário</h1>
      <div class="sub">Competência ${String(filtroMes).padStart(2, '0')}/${filtroAno} ·
        ${lctos.length} lançamento(s) · valores em R$</div>
      <table>
        <thead><tr><th>Data</th><th>Lcto</th><th>Histórico / Contas</th>
          <th class="v">Débito</th><th class="v">Crédito</th></tr></thead>
        <tbody>${linhas}</tbody>
        <tfoot><tr><td colspan="3">TOTAL DO PERÍODO</td>
          <td class="v">${fmtMoeda(totD)}</td><td class="v">${fmtMoeda(totD)}</td></tr></tfoot>
      </table>
      <p style="margin-top:26px;font-size:8pt;color:#666">
        Gerado pelo Átrio Flow em ${new Date().toLocaleString('pt-BR')} · Caio Monteiro — CRC PE-029471/O-2</p>
      </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  const alternarModo = () => setModoClassico(v => {
    const n = !v;
    try { localStorage.setItem('flow_lctos_classico', n ? '1' : '0'); } catch {}
    return n;
  });
  // Veio de outra tela com contexto (ex.: rascunhos do Fechamento)?
  const [filtroPreset] = useState(() => {
    try {
      const raw = sessionStorage.getItem('flow_lctos_filtro');
      if (raw) { sessionStorage.removeItem('flow_lctos_filtro'); return JSON.parse(raw); }
    } catch {}
    return null;
  });
  useEffect(() => {
    if (!filtroPreset) return;
    if (filtroPreset.ano)    setFiltroAno(Number(filtroPreset.ano));
    if (filtroPreset.mes)    setFiltroMes(Number(filtroPreset.mes));
  }, [filtroPreset]);

  // Abre no ultimo mes com movimento (nao no mes corrente vazio)
  useEffect(() => {
    if (!clienteId) return;
    if (filtroPreset) return;   // contexto explicito vence o padrao
    listarLancamentos(clienteId, { limit: 1 }).then(r => {
      const ult = (r?.items || [])[0];
      if (ult?.data_lcto) {
        const [a, m] = String(ult.data_lcto).slice(0, 7).split('-').map(Number);
        setFiltroAno(a); setFiltroMes(m);
      }
    }).catch(() => {});
  }, [clienteId]);
  const [filtroOrigem, setFiltroOrigem] = useState('');
  const [lctos, setLctos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mensagem, setMensagem] = useState(null);
  const [formAberto, setFormAberto] = useState(true);
  const [importAberto, setImportAberto] = useState(false);

  const carregar = () => {
    if (!clienteId) return;
    setLoading(true);
    const filtros = { ano: filtroAno, mes: filtroMes };
    if (filtroOrigem) filtros.origem_tipo = filtroOrigem;
    listarLancamentos(clienteId, filtros)
      .then(r => setLctos(r.items || []))
      .catch(err => setMensagem({ tipo: 'danger', txt: err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [clienteId, filtroAno, filtroMes, filtroOrigem]);

  const rodarWorker = async () => {
    setMensagem({ tipo: 'info', txt: 'Rodando contabilização automática Finance → lançamento…' });
    try {
      const r = await rodarContabilizacao(clienteId, `${filtroAno}-${String(filtroMes).padStart(2, '0')}`);
      setMensagem({ tipo: 'success', txt: `Worker concluído: ${r.criados} criado(s), ${r.pendentes} pendente(s) de ${r.processados} processado(s)` });
      carregar();
    } catch (err) {
      setMensagem({ tipo: 'danger', txt: err.message });
    }
  };

  if (!clienteId) return <Empty title="Selecione um cliente" />;


  return (
    <>
      <PageHeader
        title="Lançamentos contábeis"
        sub={`${lctos.length} lançamento(s) no período`}
        actions={
          <>
            <button onClick={imprimirDiario} disabled={lctos.length === 0}
              title="Gera o Livro Diário da competência para impressão">
              🖨 Imprimir diário
            </button>
            <button onClick={alternarModo}
              title="Alterna entre a lista compacta e o formato clássico do livro diário">
              {modoClassico ? '▤ Modo compacto' : '▥ Modo clássico (diário)'}
            </button>
            <button onClick={() => setFormAberto(v => !v)}>
              {formAberto ? '▴ Ocultar formulário' : '▾ Novo lançamento'}
            </button>
            <button onClick={() => setImportAberto(v => !v)}
              title="Importa lançamentos de uma planilha CSV: simula, mostra os erros e só então grava">
              ⇪ Importar planilha
            </button>
            <button onClick={rodarWorker}>↻ Rodar worker Finance</button>
          </>
        }
      />

      {importAberto && (
        <ImportadorLancamentos
          clienteId={clienteId}
          onFechar={() => setImportAberto(false)}
          onConcluido={(r) => {
            setImportAberto(false);
            setMensagem({ tipo: 'success', txt: `${r.gravados} lançamento(s) importado(s)`
              + (r.resumo?.ja_importados ? `; ${r.resumo.ja_importados} já existia(m) e foram ignorados` : '') });
            carregar();
          }}
        />
      )}

      {formAberto && (
        <FormLancamentoInline
          clienteId={clienteId}
          editando={editando}
          onCancelarEdicao={() => setEditando(null)}
          onExcluir={excluirLcto}
          onCriado={(l) => {
            setEditando(null);
            setMensagem({ tipo: 'success', txt: `Lançamento #${l.id} criado` });
            carregar();
          }}
        />
      )}

      {mensagem && <Alert tipo={mensagem.tipo}>{mensagem.txt}</Alert>}

      <div className="toolbar">
        <select value={filtroAno} onChange={e => setFiltroAno(Number(e.target.value))}>
          {anosOpcoes(5).map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={filtroMes} onChange={e => setFiltroMes(Number(e.target.value))}>
          <option value={0}>Todos os meses</option>
          {mesOpcoes().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
        </select>
        <select value={filtroOrigem} onChange={e => setFiltroOrigem(e.target.value)}>
          <option value="">Todas as origens</option>
          <option value="manual">Manual</option>
          <option value="finance">Finance</option>
          <option value="nfse">NFS-e</option>
          <option value="sieg">Sieg</option>
          <option value="dominio_folha">Domínio Folha</option>
          <option value="ajuste">Ajuste</option>
        </select>
      </div>

      <div className="card">
        {loading ? <Loading /> : lctos.length === 0 ? (
          <Empty title="Sem lançamentos no período" sub="Use o worker Finance ou crie manualmente." />
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Data</th>
                <th>Histórico</th>
                <th>Origem</th>
                {modoClassico
                  ? <><th className="num" style={{ width: 130 }}>Débito</th><th className="num" style={{ width: 130 }}>Crédito</th></>
                  : <th className="num">Valor</th>}
                <th style={{ width: 140 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {modoClassico ? lctos.flatMap(l => {
                const ps = l.partidas || [];
                return [
                  <tr key={`h${l.id}`} style={{ borderTop: '2px solid var(--border-strong)' }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{fmtData(l.data_lcto)}</td>
                    <td colSpan={2}><strong>{l.historico}</strong></td>
                    <td></td><td></td>
                    <td>
                      <button className="ghost" onClick={() => abrirEdicao(l)}>Editar</button>
                    </td>
                  </tr>,
                  ...ps.map((pa, i) => (
                    <tr key={`${l.id}-${i}`}>
                      <td></td>
                      <td colSpan={2} style={{ paddingLeft: pa.lado === 'C' ? 44 : 20 }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: 11, marginRight: 6,
                          fontVariantNumeric: 'tabular-nums' }}>{pa.conta_reduzido ?? ''}</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums', marginRight: 8 }}>{pa.conta_codigo}</span>
                        {pa.lado === 'C' ? <em>a </em> : null}{pa.conta_nome}
                      </td>
                      <td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {pa.lado === 'D' ? fmtMoeda(pa.valor) : ''}
                      </td>
                      <td className="num" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {pa.lado === 'C' ? fmtMoeda(pa.valor) : ''}
                      </td>
                      <td></td>
                    </tr>
                  )),
                ];
              }) : lctos.map(l => (
                <tr key={l.id}>
                  <td onClick={() => abrirEdicao(l)} style={{ cursor: 'pointer' }}
                      title="Clique para alterar este lançamento">
                    {fmtData(l.data_lcto)}
                  </td>
                  <td onClick={() => abrirEdicao(l)} style={{ cursor: 'pointer' }}>
                    <div>{l.historico}</div>
                    {l.origem_ref && <div className="small muted">{l.origem_ref}</div>}
                    {/* Partidas: sem ver D/C e conta nao da pra conferir o lancamento */}
                    {(l.partidas || []).length > 0 && (
                      <div className="lcto-partidas" style={{ marginTop: 6, display: 'grid', gap: 2 }}>
                        {l.partidas.map((pa, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12 }}>
                            <span style={{
                              width: 16, textAlign: 'center', fontWeight: 800, flexShrink: 0,
                              color: pa.lado === 'D' ? '#2563EB' : '#B45309',
                            }}>{pa.lado}</span>
                            {pa.conta_reduzido != null && (
                              <span style={{ minWidth: 26, textAlign: 'right', color: 'var(--text-muted)',
                                fontSize: 10.5, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                {pa.conta_reduzido}
                              </span>
                            )}
                            <span style={{ color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums',
                              flexShrink: 0 }}>{pa.conta_codigo}</span>
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pa.conta_nome}</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                              whiteSpace: 'nowrap' }}>{fmtMoeda(pa.valor)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </td>
                  <td><span className="badge badge--neutral">{l.origem_tipo}</span></td>
                  <td className="num bold">{fmtMoeda(l.valor_total)}</td>
                  <td>
                    <button className="ghost" onClick={() => abrirEdicao(l)}>Editar</button>
                    <button className="ghost" onClick={() => excluirLcto(l)}
                      style={{ color: '#EF4444' }}>Excluir</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

// =============================================================================
// Formulário inline: Lançamento contábil ágil (partida dobrada)
// Sempre visível no topo da tela. Otimizado pra lançamento em série.
// =============================================================================
function FormLancamentoInline({ clienteId, onCriado, editando, onCancelarEdicao, onExcluir }) {
  const hoje = new Date().toISOString().slice(0, 10);
  const [dataLcto, setDataLcto] = useState(hoje);
  const [historico, setHistorico] = useState('');
  const [partidas, setPartidas] = useState([
    { lado: 'D', conta_id: '', valor: '', historico_compl: '', filtro: '' },
    { lado: 'C', conta_id: '', valor: '', historico_compl: '', filtro: '' },
  ]);

  // Modo edicao: carrega o lancamento clicado no proprio formulario
  useEffect(() => {
    if (!editando) return;
    setDataLcto(String(editando.data_lcto).slice(0, 10));
    setHistorico(editando.historico || '');
    const ps = (editando.partidas || []).map(p => ({
      lado: p.lado,
      conta_id: String(p.conta_id),
      valor: String(p.valor),
      historico_compl: p.historico_compl || '',
      filtro: '',
    }));
    if (ps.length >= 2) setPartidas(ps);
  }, [editando]);
  const [contas, setContas] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    if (!clienteId) return;
    planoCliente(clienteId, { tipo: 'A' })  // só analíticas
      .then(r => setContas(r.items || []))
      .catch(e => setErro('Falha ao carregar plano de contas: ' + e.message));
  }, [clienteId]);

  const totalD = useMemo(
    () => partidas.filter(p => p.lado === 'D').reduce((s, p) => s + (Number(p.valor) || 0), 0),
    [partidas]
  );
  const totalC = useMemo(
    () => partidas.filter(p => p.lado === 'C').reduce((s, p) => s + (Number(p.valor) || 0), 0),
    [partidas]
  );
  const balanceado = Math.abs(totalD - totalC) < 0.01 && totalD > 0;

  const atualizarPartida = (idx, campo, valor) => {
    setPartidas(prev => prev.map((p, i) => i === idx ? { ...p, [campo]: valor } : p));
  };

  const adicionarPartida = (lado) => {
    setPartidas(prev => [...prev, { lado, conta_id: '', valor: '', historico_compl: '', filtro: '' }]);
  };

  const removerPartida = (idx) => {
    if (partidas.length <= 2) return;
    setPartidas(prev => prev.filter((_, i) => i !== idx));
  };

  const limpar = () => {
    // Mantém data (acelera lançamento em série dentro do mesmo dia)
    setHistorico('');
    setPartidas([
      { lado: 'D', conta_id: '', valor: '', historico_compl: '', filtro: '' },
      { lado: 'C', conta_id: '', valor: '', historico_compl: '', filtro: '' },
    ]);
    setErro(null);
  };

  // Esc cancela a edicao, como o botao. Sai pelo teclado sem tirar a mao dele.
  useEffect(() => {
    if (!editando) return;
    const aoTeclar = (ev) => {
      if (ev.key !== 'Escape' || salvando) return;
      // Alguns navegadores usam Esc para fechar o menu de um <select> aberto;
      // ali o Esc e do campo, nao da edicao.
      if (ev.target instanceof HTMLSelectElement) return;
      ev.preventDefault();
      onCancelarEdicao && onCancelarEdicao();
      limpar();
    };
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [editando, salvando, onCancelarEdicao]);

  const salvar = async () => {
    setErro(null);
    if (!historico || historico.length < 2) return setErro('Histórico obrigatório');
    if (!balanceado) return setErro(`Partida dobrada inválida: D=${totalD.toFixed(2)} ≠ C=${totalC.toFixed(2)}`);
    const partidasFinais = partidas
      .filter(p => p.conta_id && Number(p.valor) > 0)
      .map(p => ({
        lado: p.lado,
        conta_id: Number(p.conta_id),
        valor: Number(p.valor),
        historico_compl: p.historico_compl || undefined,
      }));
    if (partidasFinais.length < 2) return setErro('Mínimo 2 partidas com conta e valor');

    setSalvando(true);
    try {
      const corpo = {
        data_lcto: dataLcto,
        historico,
        origem_tipo: editando?.origem_tipo || 'manual',
        partidas: partidasFinais,
      };
      const lcto = editando
        ? await atualizarLancamento(clienteId, editando.id, corpo)
        : await criarLancamento(clienteId, corpo);
      onCriado(lcto);
      limpar();
    } catch (err) {
      setErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  // Filtra contas por busca rápida (codigo ou nome)
  const filtrarContas = (filtro) => {
    if (!filtro) return contas.slice(0, 100);
    const f = filtro.toLowerCase();
    return contas.filter(c =>
      c.codigo.toLowerCase().includes(f) || c.nome.toLowerCase().includes(f)
    ).slice(0, 100);
  };

  return (
    <div className="card" style={{
      padding: 14,
      marginBottom: 12,
      borderLeft: '3px solid var(--gold)',
    }}>
      <div className="flex" style={{ alignItems: 'center', gap: 12, marginBottom: 10 }}>
        <span style={{
          fontFamily: 'var(--font-heading)',
          fontWeight: 600, fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--gold)',
        }}>
          Novo lançamento
        </span>
        <div style={{ flex: 1 }} />
        <span className="small muted">
          {contas.length > 0 ? `${contas.length} contas disponíveis` : 'Carregando plano de contas…'}
        </span>
      </div>

      <AvisoForaDaCarteira clienteId={clienteId}
        ano={dataLcto ? Number(dataLcto.slice(0, 4)) : null}
        mes={dataLcto ? Number(dataLcto.slice(5, 7)) : null}
        acao="O que for lançado aqui" />

      {erro && <Alert tipo="danger">{erro}</Alert>}

      {/* Linha 1: data + histórico */}
      <div className="flex" style={{ gap: 8, marginBottom: 8 }}>
        <div style={{ flex: '0 0 140px' }}>
          <input
            type="date" value={dataLcto}
            onChange={e => setDataLcto(e.target.value)}
            title="Data do lançamento"
          />
        </div>
        <div style={{ flex: 1 }}>
          <input
            type="text" value={historico}
            onChange={e => setHistorico(e.target.value)}
            placeholder="Histórico — ex: Pagamento NF 123 fornecedor X"
            maxLength={500}
          />
        </div>
      </div>

      {/* Tabela compacta de partidas */}
      <table className="data" style={{ fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 70 }}>D/C</th>
            <th>Conta</th>
            <th style={{ width: 140 }} className="num">Valor</th>
            <th style={{ width: 200 }}>Complemento</th>
            <th style={{ width: 32 }}></th>
          </tr>
        </thead>
        <tbody>
          {partidas.map((p, idx) => {
            const opcoes = filtrarContas(p.filtro);
            return (
              <tr key={idx}>
                <td>
                  <select value={p.lado} onChange={e => atualizarPartida(idx, 'lado', e.target.value)}>
                    <option value="D">D — Débito</option>
                    <option value="C">C — Crédito</option>
                  </select>
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input
                      type="text"
                      placeholder="Buscar…"
                      value={p.filtro}
                      onChange={e => atualizarPartida(idx, 'filtro', e.target.value)}
                      style={{ flex: '0 0 130px', fontSize: 11 }}
                    />
                    <select
                      value={p.conta_id}
                      onChange={e => atualizarPartida(idx, 'conta_id', e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">— Selecione —</option>
                      {opcoes.map(c => (
                        <option key={c.id} value={c.id}>{c.codigo} · {c.nome}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td>
                  <input
                    type="number" step="0.01" min="0"
                    value={p.valor}
                    onChange={e => atualizarPartida(idx, 'valor', e.target.value)}
                    style={{ textAlign: 'right' }}
                    placeholder="0,00"
                  />
                </td>
                <td>
                  <input
                    type="text"
                    value={p.historico_compl}
                    onChange={e => atualizarPartida(idx, 'historico_compl', e.target.value)}
                    placeholder="opcional"
                  />
                </td>
                <td>
                  {partidas.length > 2 && (
                    <button className="ghost" onClick={() => removerPartida(idx)} title="Remover">×</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Rodapé: totais + ações */}
      <div className="flex" style={{
        marginTop: 10, gap: 16, alignItems: 'center', flexWrap: 'wrap',
      }}>
        <button className="ghost" onClick={() => adicionarPartida('D')} type="button">+ Débito</button>
        <button className="ghost" onClick={() => adicionarPartida('C')} type="button">+ Crédito</button>

        <div style={{ flex: 1 }} />

        <div className="flex" style={{ gap: 18, fontSize: 12 }}>
          <span>D <strong className="num">{fmtMoeda(totalD)}</strong></span>
          <span>C <strong className="num">{fmtMoeda(totalC)}</strong></span>
          <span style={{ color: balanceado ? 'var(--success)' : 'var(--danger)' }}>
            Δ <strong className="num">{fmtMoeda(totalD - totalC)}</strong> {balanceado ? '✓' : '✗'}
          </span>
        </div>

        {editando && (
          <>
            <span style={{ marginRight: 'auto', fontSize: 12, fontWeight: 700, color: '#B45309' }}>
              ✎ Editando o lançamento #{editando.numero ?? editando.id}
            </span>
            <button type="button" disabled={salvando}
              onClick={() => onExcluir && onExcluir(editando)}
              style={{ color: '#EF4444', borderColor: '#EF4444' }}>Excluir</button>
            <button type="button" disabled={salvando} title="Ou tecle Esc"
              onClick={() => { onCancelarEdicao && onCancelarEdicao(); limpar(); }}>Cancelar edição (Esc)</button>
          </>
        )}
        {!editando && <button onClick={limpar} type="button" disabled={salvando}>Limpar</button>}
        <button className="primary" onClick={salvar} disabled={salvando || !balanceado}>
          {salvando ? 'Salvando…' : (editando ? 'Salvar alterações' : 'Lançar (Ctrl+Enter)')}
        </button>
      </div>
    </div>
  );
}
